import clsx from 'clsx';
import { memo } from 'react';
import { useIntl } from 'react-intl';

import type { PlaceholderProps } from './types';

import { Actions } from '../Actions/Actions';
import { Layout } from '../Layout/Layout';
import { Text } from '../Text/Text';
import { PlaceholderMedia } from './subcomponents/PlaceholderMedia';
import {
  resolveDisplayDescription,
  resolveDisplayTitle,
  resolveIllustrationSrc,
  resolveTitleVariant,
} from './utils';
import './Placeholder.css';

export const Placeholder = memo(
  ({
    actions,
    description,
    icon,
    illustration,
    size = 'm',
    title,
  }: PlaceholderProps) => {
    const intl = useIntl();
    const isLoading = illustration === 'loading';
    const isIconMedia = Boolean(icon);
    const displayDescription = resolveDisplayDescription(description);
    const accessibleName = isLoading ? displayDescription : undefined;
    const illustrationSrc = illustration
      ? resolveIllustrationSrc(illustration)
      : null;
    const titleVariant = resolveTitleVariant({ isIconMedia, size });
    const displayTitle = resolveDisplayTitle(intl, {
      illustration,
      isLoading,
      title,
    });
    const shouldShowText = Boolean(displayTitle || displayDescription);

    return (
      <Layout
        align="center"
        aria-busy={isLoading ? true : undefined}
        aria-label={accessibleName}
        className={clsx(
          'eds-placeholder',
          isIconMedia
            ? 'eds-placeholder--icon'
            : [
                'eds-placeholder--illustration',
                `eds-placeholder--size-${size}`,
              ],
        )}
        direction="column"
        role={isLoading ? 'status' : undefined}
        spacing="none"
      >
        <PlaceholderMedia
          icon={icon}
          illustrationSrc={illustrationSrc}
          isLoading={isLoading}
          size={size}
        />
        {shouldShowText ? (
          <div className="eds-placeholder--text">
            {displayTitle ? (
              <Text as="p" variant={titleVariant}>
                {displayTitle}
              </Text>
            ) : null}
            {displayDescription ? (
              <Text as="p" tone="subdued" variant="body-m">
                {displayDescription}
              </Text>
            ) : null}
          </div>
        ) : null}
        {actions?.length ? (
          <Actions actions={actions} size={isIconMedia ? 's' : undefined} />
        ) : null}
      </Layout>
    );
  },
);

Placeholder.displayName = 'Placeholder';
